import { observable, action, computed, extendObservable } from "mobx";
import moment from "moment";
import Api from "~/service/Api";
import PhotoEditingJob from "~/models/PhotoEditing/PhotoEditingJob";
import Product from "~/models/Product";
import PhotoEditingImageRepository from "~/repository/PhotoEditing/PhotoEditingImageRepository";
import PhotoEditingJobRepository from "~/repository/PhotoEditing/PhotoEditingJobRepository";
import ProductIdPerJobType from "~/constants/PhotoEditing/ProductIdPerJobType";
import JobType from "~/constants/PhotoEditing/JobType";
import Immersive3dTourRepository from "~/repository/Immersive3dTour/Immersive3dTourRepository";
import HdRealImagesPerProduct from "~/constants/PhotoEditing/HdRealImagesPerProduct";

class ViewJobStore {
  /**
   * @property {string}
   */
  @observable redirectTo = "";

  /**
   * @property {boolean}
   */
  @observable isLoading = false;

  /**
   * @property {boolean}
   */
  @observable isImporting = false;

  /**
   * @property {boolean}
   */
  @observable isSavingLabel = false;

  /**
   * @property {string}
   */
  @observable label = "";

  /**
   * @property {Object|null}
   */
  @observable photoEditingJob = null;

  /**
   * @property {number|null}
   */
  photoEditingJobId = null;

  /**
   * @property {Object}
   */
  @observable photoEditingImageProducts = {};

  /**
   * @property {Product|null}
   */
  @observable photoEditingImageProduct = null;

  photoEditingImageRepository = null;

  /**
   * @property {PhotoEditingJobRepository}
   */
  photoEditingJobRepository = null;

  /**
   * @property {Immersive3dTourStore}
   */
  immersive3dTourStore = null;

  /**
   * @property {Immersive3dTourTabStore}
   */
  immersive3dTourTabStore = null;

  /**
   * @property {Immersive3dTourRepository}
   */
  immersive3dTourRepository = null;

  /**
   * @property {boolean}
   */
  @observable isImportingInImmersive3dTour = false;

  /**
   * @property {[]}
   */
  @observable importInImmersive3dTourProgressBars = [];

  /**
   * @property {boolean}
   */
  @observable hasStartedImportingInImmersive3dTour = false;

  @observable importInImmersive3dTourRequestDts = null;

  constructor(photoEditingJobId, immersive3dTourStore, immersive3dTourTabStore) {
    this.photoEditingJobId = photoEditingJobId;
    this.photoEditingJob = new PhotoEditingJob({});
    this.photoEditingImageProduct = new Product({});
    this.photoEditingImageRepository = new PhotoEditingImageRepository();
    this.photoEditingJobRepository = new PhotoEditingJobRepository();
    this.immersive3dTourStore = immersive3dTourStore;
    this.immersive3dTourTabStore = immersive3dTourTabStore;
    this.immersive3dTourRepository = new Immersive3dTourRepository();
  }

  @action
  load() {
    this.isLoading = true;

    return Promise.all([
      this.getPhotoEditingJob(),
      this.getPhotoEditingImageProduct(JobType.HD_REAL, ProductIdPerJobType[JobType.HD_REAL]),
      this.getPhotoEditingImageProduct(JobType.BASIC_HDR, ProductIdPerJobType[JobType.BASIC_HDR]),
    ]).then(() => {
      this.photoEditingImageProduct = this.photoEditingImageProducts[this.photoEditingJob.typeId];
      this.isLoading = false;
    });
  }

  @action
  getPhotoEditingJob() {
    return Api.get(`/photo-editing/jobs/${this.photoEditingJobId}`).then(response => {
      this.photoEditingJob = new PhotoEditingJob(response.data.photoEditingJob);
      // decorate images so we can know their 'selected' state.
      for (let i = 0; i < this.photoEditingJob.photoEditingImages.length; i += 1) {
        const photoEditingImage = this.photoEditingJob.photoEditingImages[i];
        // flag as selected those images that are ready
        extendObservable(photoEditingImage, {
          isSelected: photoEditingImage.isReady,
        });
      }

      this.photoEditingJob.tickEstimatedTimeLeftToFinish();
    });
  }

  @action
  getPhotoEditingImageProduct(typeId, productId) {
    return Api.get(`/products/${productId}`).then(response => {
      this.photoEditingImageProducts[typeId] = new Product(response.data.product);
    });
  }

  @action
  togglePhotoEditingImageSelection(photoEditingImageId) {
    const photoEditingImage = this.photoEditingJob.photoEditingImages.find(
      scopedPhotoEditingImage => scopedPhotoEditingImage.photoEditingImageId === photoEditingImageId,
    );

    photoEditingImage.isSelected = !photoEditingImage.isSelected;
  }

  @action
  toggleAllPhotoEditingImagesSelection() {
    const allSelected = this.photoEditingJob.photoEditingImages
      .filter(({ isReady }) => isReady)
      .every(({ isSelected }) => isSelected);

    for (let i = 0; i < this.photoEditingJob.photoEditingImages.length; i += 1) {
      const photoEditingImage = this.photoEditingJob.photoEditingImages[i];
      // only process ready images. Technically an unready image cannot be selected nor unselected.
      if (photoEditingImage.isReady) {
        photoEditingImage.isSelected = !allSelected;
      }
    }
  }

  @action
  onLabelChange(label) {
    this.label = label;
  }

  @action
  addPhotoEditingLabel() {
    if (this.label.trim() === "") {
      return Promise.resolve();
    }

    if (this.isSavingLabel) {
      return Promise.resolve();
    }

    this.isSavingLabel = true;

    return Api.post("/photo-editing/labels", {
      photoEditingJobId: this.photoEditingJobId,
      name: this.label,
    }).then(response => {
      this.label = "";
      this.isSavingLabel = false;
      this.photoEditingJob.addPhotoEditingLabel(response.data.photoEditingLabel);
    });
  }

  @action
  deletePhotoEditingLabel(photoEditingLabelId) {
    // if label doesn't exists, don't even attempt to send an xhr.
    if (!this.photoEditingJob.photoEditingLabels.find(label => label.photoEditingLabelId === photoEditingLabelId)) {
      return Promise.resolve();
    }

    // first delete the label from the collection, we don't want to show a spinner nor block the screen (loader)
    // when a label is deleted.
    this.photoEditingJob.removePhotoEditingLabelById(photoEditingLabelId);

    return Api.delete(`/photo-editing/labels/${photoEditingLabelId}`);
  }

  @computed
  get costPerItem() {
    return this.photoEditingImageProduct.discountedCreditCost;
  }

  @computed
  get totalCost() {
    const numberOfProducts =
      this.photoEditingJob.typeId === JobType.HD_REAL
        ? Math.ceil(this.photoEditingJob.numberOfProducts / HdRealImagesPerProduct)
        : this.photoEditingJob.numberOfProducts;

    return this.costPerItem * (numberOfProducts - this.photoEditingJob.numberOfFreeProducts);
  }

  @computed
  get downloadProcessedImagesUrl() {
    return this.photoEditingJobRepository.getDownloadProcessedImagesUrl(this.photoEditingJobId);
  }

  @action
  import() {
    if (this.isLoading || this.isImporting) {
      return Promise.resolve();
    }

    const selectedPhotoEditingImages = this.photoEditingJob.photoEditingImages.filter(
      ({ isReady, isSelected }) => isReady && isSelected,
    );

    if (selectedPhotoEditingImages.length === 0) {
      return Promise.resolve();
    }

    // Get selected images ids
    const imagesIds = selectedPhotoEditingImages.map(({ photoEditingImageId }) => photoEditingImageId);

    this.isImporting = true;

    return this.photoEditingImageRepository.importToTour(this.photoEditingJob.tourId, imagesIds).finally(() => {
      this.isImporting = false;
    });
  }

  @action
  async importInImmersive3dTourFromPhotoEditingImages(photoEditingImageIds) {
    this.importInImmersive3dTourRequestDts = moment.now();

    this.importInImmersive3dTourProgressBars = [];

    photoEditingImageIds.forEach(photoEditingImageId => {
      this.importInImmersive3dTourProgressBars.push({
        photoEditingImageId,
        percent: 0,
      });
    });

    this.isLoading = true;
    this.isImportingInImmersive3dTour = true;

    // This will be blocked until the resolve() function is invoked inside the Promise we created at
    // allThumbnailsReady method.
    await this.waitUntilAllSelectedPhotoEditingImagesHaveThumbnails(photoEditingImageIds);

    this.hasStartedImportingInImmersive3dTour = true;

    await this.uploadFromPhotoEditingImages(photoEditingImageIds);
  }

  allSelectedPhotoEditingImagesHaveThumbnails(photoEditingImageIds) {
    let photoEditingImage;
    for (let i = 0; i < photoEditingImageIds.length; i += 1) {
      photoEditingImage = this.photoEditingJob.photoEditingImages.find(
        image => image.photoEditingImageId === photoEditingImageIds[i],
      );

      if (photoEditingImage.thumbnailUrl === null) {
        return false;
      }
    }

    return true;
  }

  @action
  async waitUntilAllSelectedPhotoEditingImagesHaveThumbnails(photoEditingImageIds) {
    if (this.allSelectedPhotoEditingImagesHaveThumbnails(photoEditingImageIds)) {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      const check = () => {
        this.getPhotoEditingJob().then(() => {
          if (this.allSelectedPhotoEditingImagesHaveThumbnails(photoEditingImageIds)) {
            resolve();
          } else {
            setTimeout(() => check(), 5000);

            // If the user clicked at the Import in Immersive 3D Tour
            // and we are waiting for the thumbnails to be generated for more 10 minutes
            // let's inform the team
            if (
              this.importInImmersive3dTourRequestDts &&
              moment.now() - this.importInImmersive3dTourRequestDts > 600000
            ) {
              throw new Error(
                `Thumbnail generation for Photo Editing Job #${this.photoEditingJob.photoEditingJobId} has taken longer than expected.`,
              );
            }
          }
        });
      };

      check();
    });
  }

  async uploadFromPhotoEditingImages(photoEditingImageIds) {
    for (let i = 0; i < photoEditingImageIds.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await this.uploadFromPhotoEditingImage(photoEditingImageIds[i]);
    }

    this.immersive3dTourTabStore.forceShowUpload = false;
    this.immersive3dTourTabStore.refreshPanoramas();

    this.isLoading = false;
    this.isImportingInImmersive3dTour = false;
    this.hasStartedImportingInImmersive3dTour = false;
  }

  async uploadFromPhotoEditingImage(photoEditingImageId) {
    return this.immersive3dTourRepository
      .uploadFromPhotoEditingImage(this.photoEditingJob.tourId, photoEditingImageId)
      .then(response => {
        this.immersive3dTourTabStore.onFileUploaded({ name: response.data.name }, response);

        const photoEditingImage = this.photoEditingJob.photoEditingImages.find(
          image => image.photoEditingImageId === parseInt(response.data.photoEditingImageId, 10),
        );

        if (photoEditingImage.thumbnailUrl !== null) {
          this.immersive3dTourTabStore.thumbnails[response.data.id] = photoEditingImage.thumbnailUrl;
        }

        const importInImmersive3dTourProgressBar = this.importInImmersive3dTourProgressBars.find(
          progressBar => progressBar.photoEditingImageId === photoEditingImage.photoEditingImageId,
        );

        importInImmersive3dTourProgressBar.percent = 100;
      });
  }
}

export default ViewJobStore;
